import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import API_CONFIG from '../config/api';
import { useBranding, useDocumentBranding } from '../utils/loginBranding';
import './AdminLoginPage.css';
import './ForgotPasswordPage.css';

function ResetPasswordPage() {
    // Browser-tab title and icon follow Settings → Login Page Branding.
    const brand = useBranding();
    useDocumentBranding(brand, `${brand.brandName} Admin Portal`);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/api/admin/reset-password-with-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                setError(data.message || 'Could not reset password. The link may have expired.');
                return;
            }

            setSuccess(true);
        } catch (err) {
            setError('Server connection failed. Please check your network and try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="admin-login-page">
            <div className="admin-login-bg">
                <img src={`${process.env.PUBLIC_URL || ''}/admin-login-bg.png`} alt="" aria-hidden="true" />
            </div>

            <div className="auth-page-center">
                <div className="admin-login-form-container">
                    <div className="admin-login-card">
                        <div className="admin-login-header">
                            <h1 className="admin-page-title">Reset Password</h1>
                            <p className="admin-page-subtitle">
                                {success ? 'Your password has been updated.' : 'Choose a new password for your admin account.'}
                            </p>
                        </div>

                        {error && (
                            <div className="admin-alert">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}

                        {!token ? (
                            <div className="auth-success-state">
                                <p>This reset link is missing its token. Please request a new one.</p>
                                <Link to="/admin/forgot-password" className="auth-link-btn">Request a new link</Link>
                            </div>
                        ) : success ? (
                            <div className="auth-success-state">
                                <div className="auth-success-icon">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                    </svg>
                                </div>
                                <p>You can now sign in with your new password.</p>
                                <button type="button" className="admin-submit-btn" onClick={() => navigate('/admin/login')}>
                                    <div className="btn-content"><span>Go to Sign In</span></div>
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="admin-form">
                                <div className="admin-input-group">
                                    <label htmlFor="newPassword">New Password</label>
                                    <div className="input-with-icon">
                                        <div className="input-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                        </div>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="newPassword"
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="At least 8 characters"
                                            disabled={isLoading}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={isLoading}
                                        >
                                            {showPassword ? '🙈' : '👁️'}
                                        </button>
                                    </div>
                                </div>

                                <div className="admin-input-group">
                                    <label htmlFor="confirmPassword">Confirm Password</label>
                                    <div className="input-with-icon">
                                        <div className="input-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                            </svg>
                                        </div>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            id="confirmPassword"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            placeholder="Re-enter password"
                                            disabled={isLoading}
                                            required
                                        />
                                    </div>
                                </div>

                                <button type="submit" className="admin-submit-btn" disabled={isLoading}>
                                    {isLoading ? (
                                        <div className="btn-content">
                                            <div className="admin-spinner"></div>
                                            <span>Updating...</span>
                                        </div>
                                    ) : (
                                        <div className="btn-content">
                                            <span>Reset Password</span>
                                        </div>
                                    )}
                                </button>
                            </form>
                        )}

                        {!success && (
                            <div className="auth-back-link">
                                <Link to="/admin/login">← Back to Sign In</Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ResetPasswordPage;
